import AppHeader from "@/components/AppHeader";
import InformationDisplay, { displayInformation, formatInformationTime, informationDisplayLabel } from "@/components/InformationDisplay";
import NewsRelevanceDisplay from "@/components/NewsRelevanceDisplay";
import { isAlertEligibleInformation, isUsableInformation, type InformationState } from "@shared/informationState";
import { useSignals } from "@/hooks/useNews";
import { type NormalizedSignal, type SignalConfidence } from "@/lib/types";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, CheckCircle, ExternalLink, Loader2, RefreshCw, XCircle } from "lucide-react";
import React, { useState } from "react";

// ---------------------------------------------------------------------------
// Status / evidence-quality badge helpers
// ---------------------------------------------------------------------------

const CONFIDENCE_COLOR: Record<SignalConfidence, string> = {
  high: "#ff9100",
  medium: "#aaa",
  low: "#555",
};

const SEVERITY_ORDER: Record<NormalizedSignal["severity"], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function signalRank(signal: NormalizedSignal): number {
  const state = displayInformation(signal.information);
  return state && isAlertEligibleInformation(state) ? 0 : state && isUsableInformation(state) ? 1 : 2;
}

function StatusBadge({ information }: { information?: InformationState }) {
  const state = displayInformation(information);
  const color = state?.provider.failure ? "#ff4444" : state && isAlertEligibleInformation(state) ? "#00cc66" : "#aaa";
  return <span style={{ color, fontSize: 9, fontWeight: 800, border: "1px solid", padding: "1px 5px", display: "inline-block" }}>{informationDisplayLabel(information)}</span>;
}

function EvidenceQualityDot({ tier, legacy }: { tier: SignalConfidence; legacy: boolean }) {
  return (
    <span
      title={`${legacy ? "Legacy confidence snapshot" : "Evidence quality"}: ${tier}`}
      style={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: CONFIDENCE_COLOR[tier] ?? "#555",
        flexShrink: 0,
        marginTop: 2,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Category filter
// ---------------------------------------------------------------------------

const CATEGORY_GROUPS: { label: string; categories: string[] }[] = [
  { label: "All", categories: [] },
  { label: "Markets", categories: ["markets", "fixed-income", "forex", "energy", "crypto", "macro"] },
  { label: "Macro", categories: ["macro", "regulation", "supply-chain"] },
  { label: "Geo / Events", categories: ["geopolitics", "weather", "earthquake", "health", "market-news"] },
];

// ---------------------------------------------------------------------------
// Signal row
// ---------------------------------------------------------------------------

function SignalRow({ signal }: { signal: NormalizedSignal }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        borderBottom: "1px solid #1a1a1a",
        padding: "10px 0",
        cursor: "pointer",
      }}
      onClick={() => setExpanded((v) => !v)}
    >
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <EvidenceQualityDot tier={signal.evidenceQuality?.tier ?? signal.confidence} legacy={!signal.evidenceQuality} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 3 }}>
            <StatusBadge information={signal.information} />
            <span style={{ color: "#ff9100", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              {signal.category}
            </span>
            <span style={{ color: "#555", fontSize: 10 }}>{signal.source}</span>
          </div>
          <div style={{ color: "#f0f0f0", fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
            {signal.title}
          </div>
          <InformationDisplay information={signal.information} />
          <p style={{ color: "#777", fontSize: 10, margin: "4px 0 0" }}>
            {signal.evidenceQuality
              ? `Evidence quality: ${signal.evidenceQuality.tier}${signal.evidenceQuality.basis ? ` · ${signal.evidenceQuality.basis}` : ""}`
              : `Legacy confidence snapshot: ${signal.confidence}`}
            {" · Analytical confidence: unassessed"}
          </p>
          <NewsRelevanceDisplay relevance={signal.relevance} newsEvidence={signal.newsEvidence} />
          {expanded && (
            <div style={{ marginTop: 8 }}>
              <p style={{ color: "#aaa", fontSize: 12, lineHeight: 1.5, margin: "0 0 6px" }}>
                {signal.summary}
              </p>
              {signal.marketImpact && !signal.relevance && !signal.newsEvidence?.length && (
                <p style={{ color: "#888", fontSize: 11, margin: "0 0 6px" }}>
                  <span style={{ color: "#ff9100", fontWeight: 700 }}>Impact: </span>
                  {signal.marketImpact}
                </p>
              )}
              {signal.location && (
                <p style={{ color: "#666", fontSize: 11, margin: "0 0 6px" }}>
                  Location: {signal.location}
                </p>
              )}
              {signal.requiresKey && (
                <p style={{ color: "#555", fontSize: 11, margin: "0 0 6px" }}>
                  Requires key: {signal.requiresKey}
                </p>
              )}
              {signal.notes && (
                <p style={{ color: "#555", fontSize: 11, fontStyle: "italic", margin: "0 0 6px" }}>
                  {signal.notes}
                </p>
              )}
              {signal.url && (
                <a
                  href={signal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#ff9100", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Source <ExternalLink size={10} />
                </a>
              )}
            </div>
          )}
        </div>
        <div style={{ color: "#333", fontSize: 11, flexShrink: 0 }}>
          {signal.severity === "critical" && <AlertTriangle size={14} color="#ff4444" />}
          {signal.severity === "high" && <AlertTriangle size={14} color="#ff9100" />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Signals: React.FC = () => {
  const { data, isLoading, isError, refetch, isFetching } = useSignals();
  const [groupFilter, setGroupFilter] = useState(0);

  const allSignals = data?.signals ?? [];
  const activeGroup = CATEGORY_GROUPS[groupFilter];
  const eligibleCount = allSignals.filter((signal) => signalRank(signal) === 0).length;
  const sourceIssues = data?.sources?.filter((source) => {
    const state = displayInformation(source.information);
    return state?.provider.health === "failed" || state?.provider.health === "degraded" || state?.data.availability === "warming" || state?.provider.activity === "cooldown";
  }) ?? [];
  const filtered =
    activeGroup.categories.length === 0
      ? allSignals
      : allSignals.filter((s) => activeGroup.categories.includes(s.category));

  const sorted = [...filtered].sort((a, b) => {
    const sd = signalRank(a) - signalRank(b);
    if (sd !== 0) return sd;
    return SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  });

  return (
    <>
      <Helmet>
        <title>Signals — Source-Backed Market Signals | Tylee Smart Markets</title>
        <meta name="description" content="Normalized market, macro, geo, and OSINT signals with freshness and provenance metadata. Source records from NOAA, USGS, GDELT, WHO, and finance data providers." />
        <link rel="canonical" href="https://tyl.ee/signals" />
      </Helmet>

      <div className="tsm-home-shell theme-amber" style={{ minHeight: "100vh" }}>
        <a href="#signals-main" className="skip-link">Skip to main content</a>
        <AppHeader brandText="Signals" />

        <main id="signals-main" style={{ maxWidth: 960, margin: "0 auto", padding: "24px 18px" }}>
          {/* Header row */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <p style={{ color: "#ff9100", fontWeight: 800, letterSpacing: "0.08em", margin: 0, fontSize: 11, textTransform: "uppercase" }}>
                TSM SIGNALS
              </p>
              <h1 style={{ color: "#fff", fontSize: 22, fontWeight: 800, margin: "4px 0 0", lineHeight: 1.1 }}>
                Source-Backed Signal Queue
              </h1>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
              {data && (
                <span style={{ color: "#555", fontSize: 11 }}>
                  {eligibleCount}/{data.totalCount} alert-eligible
                </span>
              )}
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                style={{
                  background: "none",
                  border: "1px solid #333",
                  color: "#888",
                  padding: "5px 10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 11,
                }}
              >
                <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Provenance legend */}
          <div style={{ display: "flex", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
            <span style={{ color: "#888", fontSize: 10 }}>Observation age, provider health, and delivery mode are shown separately. Alert-eligible records require validated current observations and a healthy source.</span>
          </div>
          {data && data.eventTracking && data.eventTracking.status !== "tracked" && (
            <p role="status" style={{ color: "#a3a3a3", fontSize: 11, margin: "0 0 14px" }}>
              Event tracking {data.eventTracking.status}; source records remain visible, but group chronology and group counts are unavailable.
              {data.eventTracking.reason ? ` ${data.eventTracking.reason}` : ""}
            </p>
          )}
          {data && !data.eventTracking && (
            <p role="status" style={{ color: "#a3a3a3", fontSize: 11, margin: "0 0 14px" }}>
              Event tracking metadata unavailable; source records remain visible, but group chronology and group counts are unavailable.
            </p>
          )}

          {data?.sources ? <details style={{ color: "#aaa", fontSize: 11, marginBottom: 18 }}>
            <summary>Source states ({data.sources.length}){sourceIssues.length ? ` · ${sourceIssues.map((source) => `${source.source}: ${informationDisplayLabel(source.information)}`).join("; ")}` : ""}</summary>
            {data.sources.map((source) => <div key={source.id} style={{ borderBottom: "1px solid #222", padding: "8px 0" }}>
              <strong>{source.source}</strong>
              <InformationDisplay information={source.information} />
            </div>)}
          </details> : data && <p style={{ color: "#888", fontSize: 11 }}>Source coverage metadata unavailable.</p>}

          {/* Category filter tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 18, borderBottom: "1px solid #1a1a1a", paddingBottom: 10 }}>
            {CATEGORY_GROUPS.map((g, i) => (
              <button
                key={g.label}
                onClick={() => setGroupFilter(i)}
                style={{
                  background: groupFilter === i ? "#ff9100" : "none",
                  border: "1px solid",
                  borderColor: groupFilter === i ? "#ff9100" : "#2a2a2a",
                  color: groupFilter === i ? "#000" : "#888",
                  padding: "4px 12px",
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: "pointer",
                  letterSpacing: "0.06em",
                }}
              >
                {g.label}
              </button>
            ))}
          </div>

          {/* Body */}
          {isLoading ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#555", padding: "40px 0" }}>
              <Loader2 size={16} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Fetching signals from all sources…</span>
            </div>
          ) : isError ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#ff4444", padding: "40px 0" }}>
              <XCircle size={16} />
              <span style={{ fontSize: 13 }}>Failed to load signals. Check server logs.</span>
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ display: "flex", gap: 10, alignItems: "center", color: "#555", padding: "40px 0" }}>
              <CheckCircle size={16} />
              <span style={{ fontSize: 13 }}>No signal records in this category.</span>
            </div>
          ) : (
            <div>
              {sorted.map((signal) => (
                <SignalRow key={signal.id} signal={signal} />
              ))}
            </div>
          )}

          {/* Footer metadata */}
          {data && (
            <div style={{ marginTop: 24, borderTop: "1px solid #1a1a1a", paddingTop: 12 }}>
              <p style={{ color: "#444", fontSize: 10, margin: 0 }}>
                Response assembled {formatInformationTime(data.generatedAt)} · {data.totalCount} signals · {eligibleCount} alert-eligible
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default Signals;
